import { Result } from "antd";
import CustomTable from "components/CustomTable";
import PageHeader from "components/PageHeader";
import { LOYALTY_PACKAGE_STATUS } from "features/LoyaltyPackage/core/constants";
import {
  LoyaltyDetailEditButton,
  LoyaltyDetailMetaDivider,
  LoyaltyDetailMetaField,
  LoyaltyDetailMetaRow,
  LoyaltyDetailSummary,
  LoyaltySection,
  LoyaltyStatusBadge,
} from "features/LoyaltyPackage/presentation/components";
import {
  calcMilestoneMaxQty,
  formatLoyaltyDate,
  formatProductMilestoneSummary,
  getRewardUnitDisplayLabel,
} from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import { Link, useHistory, useParams } from "react-router-dom";
import usePackageDetail from "./usePackageDetail";

import "./index.scss";

function PackageDetail() {
  const { id } = useParams();
  const history = useHistory();

  const { period, periodLoading, milestones, products, productsLoading } = usePackageDetail(id);

  const isEditable = period?.status === LOYALTY_PACKAGE_STATUS.UPCOMING;

  const milestoneColumns = [
    {
      title: t("loyaltyPackage.packageDetail.colMilestone"),
      key: "index",
      width: 80,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: t("loyaltyPackage.packageDetail.colAwardAmountTitle"),
      dataIndex: "awardAmountTitle",
      key: "awardAmountTitle",
      align: "center",
      render: (value) => value ?? "—",
    },
    {
      title: t("loyaltyPackage.milestone.minQty"),
      dataIndex: "minQty",
      key: "minQty",
      align: "center",
      render: (value) => value ?? "—",
    },
    {
      title: t("loyaltyPackage.milestone.maxQty"),
      key: "maxQty",
      align: "center",
      render: (_, record, index) => {
        const maxQty = record.maxQty ?? calcMilestoneMaxQty(milestones, index);
        return maxQty ?? "—";
      },
    },
    {
      title: t("loyaltyPackage.milestone.rewardUnit"),
      dataIndex: "rewardUnit",
      key: "rewardUnit",
      align: "center",
      render: (unit) => getRewardUnitDisplayLabel(unit),
    },
    {
      title: t("loyaltyPackage.packageDetail.colDisplayPercent"),
      dataIndex: "displayPercent",
      key: "displayPercent",
      align: "center",
      render: (value) => (value != null ? value : "—"),
    },
  ];

  const indexWidth = 72;
  const codeWidth = 200;
  const restColWidth = `calc((100% - ${indexWidth + codeWidth}px) / 2)`;

  const productColumns = [
    {
      title: t("common.index"),
      key: "index",
      width: indexWidth,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: t("loyaltyPackage.packageForm.productCode"),
      dataIndex: "productCode",
      key: "productCode",
      width: codeWidth,
      align: "center",
      render: (code, record) => {
        if (!code) return "—";
        return (
          <Link to={`/product/detail/${record.productId}`} target="_blank" className="loyalty-package-link">
            {code}
          </Link>
        );
      },
    },
    {
      title: t("loyaltyPackage.packageForm.appliedProductColumn"),
      dataIndex: "productName",
      key: "productName",
      width: restColWidth,
      render: (name) => name ?? "—",
    },
    {
      title: t("loyaltyPackage.packageForm.discountPerProduct"),
      key: "discount",
      width: restColWidth,
      align: "center",
      render: (_, record) => {
        const summary =
          record.discountSummary ?? formatProductMilestoneSummary(record, milestones, { requireAllFilled: false });
        if (!summary.length) return "—";
        return (
          <div className="package-detail-product-discount-lines">
            {summary.map((line, idx) => (
              <span key={idx} className="package-detail-product-discount-item">
                {line}
              </span>
            ))}
          </div>
        );
      },
    },
  ];

  if (!periodLoading && !period) {
    return (
      <div className="package-detail-container">
        <PageHeader
          pageTitle={t("loyaltyPackage.packageDetail.title")}
          routes={[
            { path: "/loyalty-package?tab=package", name: t("loyaltyPackage.packageForm.breadcrumbParent") },
            { path: "", name: t("loyaltyPackage.packageDetail.title") },
          ]}
        />
        <Result status="404" title={t("loyaltyPackage.packageDetail.notFound")} />
      </div>
    );
  }

  return (
    <div className="package-detail-container">
      <PageHeader
        pageTitle={t("loyaltyPackage.packageDetail.title")}
        routes={[
          { path: "/loyalty-package?tab=package", name: t("loyaltyPackage.packageForm.breadcrumbParent") },
          { path: "", name: t("loyaltyPackage.packageDetail.title") },
        ]}
      />

      <LoyaltyDetailSummary
        loading={periodLoading}
        title={period?.title}
        meta={
          <LoyaltyDetailMetaRow>
            {period?.status && <LoyaltyStatusBadge status={period.status} />}
            <LoyaltyDetailMetaDivider />
            <LoyaltyDetailMetaField label={t("loyaltyPackage.packageDetail.timePeriod")}>
              {t("loyaltyPackage.programForm.from")} {formatLoyaltyDate(period?.startTime)}{" "}
              {t("loyaltyPackage.programForm.to")} {formatLoyaltyDate(period?.endTime)}
            </LoyaltyDetailMetaField>
            <LoyaltyDetailMetaDivider />
            <LoyaltyDetailMetaField label={t("loyaltyPackage.form.category")}>
              {period?.category?.name ?? "—"}
            </LoyaltyDetailMetaField>
          </LoyaltyDetailMetaRow>
        }
        action={
          <LoyaltyDetailEditButton
            label={t("loyaltyPackage.packageDetail.edit")}
            disabled={!isEditable}
            disabledTooltip={t("loyaltyPackage.packageDetail.editDisabled")}
            onClick={() => history.push(`/loyalty-package/package/edit/${id}`)}
          />
        }
      />

      <LoyaltySection
        title={t("loyaltyPackage.packageDetail.milestoneSection")}
        spaced
        bodyClassName="custom-table-list-loyalty"
      >
        <CustomTable
          columns={milestoneColumns}
          dataSource={milestones}
          rowKey={(record, index) => `${record.id}-${index}`}
          loading={periodLoading}
          pagination={false}
          scroll={{ x: "max-content" }}
          className="package-detail-milestone-table"
        />
      </LoyaltySection>

      <LoyaltySection title={t("loyaltyPackage.form.productSection")} spaced bodyClassName="custom-table-list-loyalty">
        <CustomTable
          columns={productColumns}
          dataSource={products}
          rowKey="productId"
          loading={productsLoading || periodLoading}
          pagination={false}
          scroll={null}
          tableLayout="fixed"
          className="package-detail-product-table"
        />
      </LoyaltySection>
    </div>
  );
}

export default PackageDetail;
